import { useI18n } from 'vue-i18n-composable';

export default function useNumberFormatter() {
  const { t } = useI18n();

  function formatCurrency(number, currency, locales = [], options = {}) {
    if (currency === 'EUR' || currency === 'USD') {
      return new Intl.NumberFormat(
        'en',
        { ...{ style: 'currency', minimumFractionDigits: 0, currency }, ...options },
      ).format(Math.abs(number));
    }
    return new Intl.NumberFormat(
      locales,
      { ...{ style: 'currency', minimumFractionDigits: 0, currency }, ...options },
    ).format(Math.abs(number));
  }

  function formatCurrencyFromMoney({ value, currency }) {
    return formatCurrency(value, currency);
  }

  function formatCurrencyRange(
    from,
    to,
    currency,
    locales = [],
    options = {},
  ) {
    const fromFormatted = formatCurrency(from, currency, locales, options);
    const currencySymbol = formatCurrency(0, currency, locales).replace('0', '').trim();

    if (fromFormatted.startsWith(currencySymbol)) {
      return {
        from: fromFormatted,
        to: formatCurrency(to, currency, locales, options).trim(),
      };
    }

    return {
      from: fromFormatted.replace(currencySymbol, '').trim(),
      to: formatCurrency(to, currency, locales, options),
    };
  }

  function formatSalary(salary) {
    if (!salary || (!salary.from && !salary.to)) {
      return t('notProvided');
    }

    if (salary.from === salary.to) {
      return salary.currency === 'RUB'
        ? `${salary.from} ${salary.currencySymbol} ${t(salary.payFrequency)} ${t(salary.payType)}`
        : `${salary.currencySymbol} ${salary.from} ${t(salary.payFrequency)} ${t(salary.payType)}`;
    }

    if (!salary.from) {
      return salary.currency === 'RUB'
        ? `${t('to')} ${salary.to} ${salary.currencySymbol} ${t(salary.payFrequency)} ${t(salary.payType)}`
        : `${t('to')} ${salary.currencySymbol} ${salary.to} ${t(salary.payFrequency)} ${t(salary.payType)}`;
    }

    if (!salary.to) {
      return salary.currency === 'RUB'
        ? `${t('from')} ${salary.from} ${salary.currencySymbol} ${t(salary.payFrequency)} ${t(salary.payType)}`
        : `${t('from')} ${salary.currencySymbol} ${salary.from} ${t(salary.payFrequency)} ${t(salary.payType)}`;
    }

    return salary.currency === 'RUB'
      ? `${t('from')} ${salary.from} ${salary.currencySymbol} ${t('to')}
        ${salary.to} ${salary.currencySymbol} ${t(salary.payFrequency)} ${t(salary.payType)}`
      : `${t('from')} ${salary.currencySymbol} ${salary.from} ${t('to')}
        ${salary.currencySymbol} ${salary.to} ${t(salary.payFrequency)} ${t(salary.payType)}`;
  }

  function formatSalaryShort(salary) {
    if (!salary || (!salary.from && !salary.to)) {
      return t('notProvided');
    }

    if (salary.from === salary.to) {
      return salary.currency === 'RUB'
        ? `${salary.from} ${salary.currencySymbol}`
        : `${salary.currencySymbol} ${salary.from}`;
    }

    if (!salary.from) {
      return salary.currency === 'RUB'
        ? `${t('to')} ${salary.to} ${salary.currencySymbol}`
        : `${t('to')} ${salary.currencySymbol} ${salary.to}`;
    }

    if (!salary.to) {
      return salary.currency === 'RUB'
        ? `${t('from')} ${salary.from} ${salary.currencySymbol}`
        : `${t('from')} ${salary.currencySymbol} ${salary.from}`;
    }

    return salary.currency === 'RUB'
      ? `${t('from')} ${salary.from} ${salary.currencySymbol} ${t('to')}
        ${salary.to} ${salary.currencySymbol}`
      : `${t('from')} ${salary.currencySymbol} ${salary.from} ${t('to')}
        ${salary.currencySymbol} ${salary.to}`;
  }

  return {
    formatCurrency,
    formatCurrencyRange,
    formatSalary,
    formatCurrencyFromMoney,
    formatSalaryShort,
  };
}
